import React, { useEffect } from "react";
import { TabType } from "@constants";
import { Icon } from "@partials";
import { getLocalization } from "@utilities";

import { LocalizationTexts } from "../types";

type LayoutContainerProps = {
  children: React.ReactNode;
};

type TabProps = {
  id: string;
  dataTestId: string;
  iconName: IconName;
  iconViewbox: string;
  iconAlt: string;
  iconSvg: string;
  localization: LocalizationTexts;
  tabText: keyof LocalizationTexts;
};

type TabsBranchesAtmsProps = {
  iconsList: any;
  localization: LocalizationTexts;
};

const ID_BRANCHES_CONTAINER = "js-branches-container";
const ID_ATMS_CONTAINER = "js-atms-container";
const HIDDEN_CLASS = "is-hidden";
const ID_BRANCHES_BTN = "branches-btn";
const ID_ATMS_BTN = "atms-btn";
const ACTIVE_CLASS = "is-active";

const LayoutContainer: React.FC<LayoutContainerProps> = ({ children }) => {
  return (
    <div className="container">
      <div className="row">
        <div className="sm:col-22 sm:offset-1">{children}</div>
      </div>
    </div>
  );
};

const Tab: React.FC<TabProps> = ({
  id,
  dataTestId,
  iconName,
  iconViewbox,
  iconAlt,
  iconSvg,
  localization,
  tabText,
}) => {
  return (
    <li className="tabs-branches-atms__list-item">
      <button type="button" id={id} data-testid={dataTestId} className="tabs-branches-atms__button">
        <Icon
          name={iconName}
          viewBox={iconViewbox}
          alt={iconAlt}
          svg={iconSvg}
          className="icon mb-2 inline h-6 w-6 shrink-0 fill-red-corporate sm:mb-0 sm:mr-2"
        />
        <div
          className={iconName === "branch-atm-atm" ? "" : "words-break"}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: getLocalization(localization, tabText) }}
        />
      </button>
    </li>
  );
};

const TabsBranchesAtms: React.FC<TabsBranchesAtmsProps> = ({ localization, iconsList }) => {
  const setLocalStorageIsActiveTab = (type: TabType, value: string) => {
    window.sessionStorage.setItem(
      type === TabType.Branches
        ? `active_tab_is_${TabType.Branches}`
        : `active_tab_is_${TabType.Atms}`,
      value
    );
  };

  // Helper function to update URL with tab query parameter
  const updateUrlWithTabParam = (tab: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    window.history.replaceState(null, "", url.toString());
  };

  useEffect(() => {
    // containers
    const containerBranches = document.getElementById(ID_BRANCHES_CONTAINER);
    const containerAtms = document.getElementById(ID_ATMS_CONTAINER);

    const tabBranches = document.getElementById(ID_BRANCHES_BTN);
    const tabAtms = document.getElementById(ID_ATMS_BTN);

    // Set visibility of containers based on the selected tab
    const showBranches = () => {
      containerBranches.classList.remove(HIDDEN_CLASS);
      containerAtms.classList.add(HIDDEN_CLASS);
    };

    const showAtms = () => {
      containerAtms.classList.remove(HIDDEN_CLASS);
      containerBranches.classList.add(HIDDEN_CLASS);
    };

    // Update active tab classes
    const activateBranchesTab = () => {
      tabBranches.classList.add(ACTIVE_CLASS);
      tabAtms.classList.remove(ACTIVE_CLASS);
      showBranches();
    };

    const activateAtmsTab = () => {
      tabAtms.classList.add(ACTIVE_CLASS);
      tabBranches.classList.remove(ACTIVE_CLASS);
      showAtms();
    };

    // Handle tab click events
    tabBranches.addEventListener("click", () => {
      activateBranchesTab();
      setLocalStorageIsActiveTab(TabType.Branches, "true");
      setLocalStorageIsActiveTab(TabType.Atms, "false");
      updateUrlWithTabParam("branches");
    });

    tabAtms.addEventListener("click", () => {
      activateAtmsTab();
      setLocalStorageIsActiveTab(TabType.Atms, "true");
      setLocalStorageIsActiveTab(TabType.Branches, "false");
      updateUrlWithTabParam("atms");
    });

    // Check the query parameter and set the active tab on page load
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");

    if (tabParam === "atms") {
      activateAtmsTab();
    } else if (tabParam === "branches" || !tabParam) {
      activateBranchesTab();
    }
  }, []);

  const { bank, branchAtmAtm } = iconsList;

  return (
    <LayoutContainer>
      <ul className="tabs-branches-atms">
        <Tab
          id={ID_BRANCHES_BTN}
          dataTestId={`tab-${ID_BRANCHES_BTN}`}
          iconName={bank.name}
          iconViewbox={bank.viewbox}
          iconAlt={bank.alt}
          iconSvg={bank.svg}
          localization={localization}
          tabText="Branches_BranchPlural"
        />
        <Tab
          id={ID_ATMS_BTN}
          dataTestId={`tab-${ID_ATMS_BTN}`}
          iconName={branchAtmAtm.name}
          iconViewbox={branchAtmAtm.viewbox}
          iconAlt={branchAtmAtm.alt}
          iconSvg={branchAtmAtm.svg}
          localization={localization}
          tabText="Branches_AtmPlural"
        />
      </ul>
    </LayoutContainer>
  );
};

export default TabsBranchesAtms;
